import React, {useEffect, useState} from 'react';
import { useLocation } from 'react-router-dom';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import {fetchHelper} from "../helpers/fetchHelper";
import LocalStorageHelper from "../helpers/LocalStorageHelper";
import { Helmet } from 'react-helmet'

interface UserAuthenticationDto {
    token: string;
    fName: string;
    sName: string;
    clientId: number;
    email: string;
    isBillingRestricted: boolean;
    isSuccessful: boolean;
    reason: string;
    requires2FA: boolean;
}

interface VerifyReq {
    clientId: number;
    code: string;
}
interface VerifyRes {                     
    jwt?: string;                         
    redirectUrl?: string;                 
}

interface TwoFactorVerifyProps {
    onSuccess?: (apiResponse: VerifyRes) => void;
}

const TwoFactorVerify: React.FC<TwoFactorVerifyProps> = ({ onSuccess }) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const clientId = parseInt(queryParams.get('clientId') || '-1', 10);
    const persist = queryParams.get('persist') === 'true';

    const [code,      setCode]      = useState('');
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState<string | null>(null);
    
    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (isNaN(clientId) || clientId <= 0) {
            setError('Invalid client identification');
            return;
        }
        
        if (code.trim().length === 0) return;

        setLoading(true);
        setError(null);
        try {
            const body: VerifyReq = { 
                clientId: clientId,
                code: code.replace(/\s+/g, '') 
            };
            const res = await fetchHelper.postJson<UserAuthenticationDto, VerifyReq>('/api/2fa/loginverify', body);

            if (res.status >= 200 && res.status < 300) {
                // onSuccess?.(res.data ?? {});

                
                // probably should refactor this and the code in UserService.ts to avoid duplication, however
                // the code in UserService looked like it could have a bug (around the persist flag - haven't tested yet)
                // so i've just duplicated and fixed here for now
                let user = res.data
                if (user && user.token) {
                    LocalStorageHelper.setItem<UserAuthenticationDto>('user', user)

                    window.LOQ = window.LOQ || []
                    window.LOQ.push(['ready', async LO => {
                        await LO.$internal.ready('visitor')
                        LO.visitor.identify({
                            email: user.email,
                            name: `${user.fName} ${user.sName}`,
                            id: `${user.clientId}`
                        })
                    }])

                    LocalStorageHelper.setItem<boolean>('billingRestricted', user.isBillingRestricted)
                }

                if (!persist) {
                    LocalStorageHelper.removeItem('user')
                    LocalStorageHelper.removeItem('userDetails')
                }

                window.location.href = '/customer/dashboard';
            } else {
                setError('Invalid code. Please try again.');
            }
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <Helmet>
				<style type="text/css">{`
                       footer{
                       padding-top:20px;
                       background-color: transparent !important;}
                    `}</style>
			</Helmet>
        <div className="p-4 border brad-10 shadow-lg bg-white mt-100 mb-100" style={{ maxWidth: 380, margin: '0 auto' }}>
            <div className='text-center mb-4'>
                <h4 className="text-center text-500 mb-5">Two-Factor Verification</h4>
                <p>Enter the 6-digit code from your authenticator app.</p>
            </div>
            <Form onSubmit={submit}>
                <Form.Group>
                    <Form.Control
                        type="text"
                        inputMode="numeric"
                        autoFocus
                        maxLength={6}
                        className="text-center fw-bold text-lg"
                        placeholder="eg. 123456"
                        value={code}
                        onChange={e => setCode(e.target.value)}
                    />
                </Form.Group>

                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

                <Button
                    type="submit"
                    size='lg'
                    variant="cta"
                    className="w-100 mt-4"
                    disabled={loading || code.length !== 6}
                >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Verify & Continue'}
                </Button>
            </Form>
        </div>

        </>
    );
};

export default TwoFactorVerify;
